import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Building2, AlertCircle, ShieldCheck, Shield, Eye, Crown, LogOut } from 'lucide-react';
import { api, parseApiError, type OrganizationSummary, type OrganizationRole } from '../lib/api';
import { orgRoleDisplayName } from '../lib/roles';
import { orgSetupReturnPath } from '../lib/setupJump';
import { Card } from './Card';
import { MORPH } from '../lib/motion';

// ─────────────────────────────────────────────────────────────────────────────
// OrgPanel — the org identity card (name, role badge, quick links, leave).
// Extracted from MyOrg so the same card heads both the Setup Organisation
// step and any membership-context surface. Membership-only affordances
// (role badge, joined date, request-elevation, leave) render only when the
// caller supplies the membership context; on membership-less surfaces
// (e.g. founder on Setup) the card degrades to identity + quick links.
// ─────────────────────────────────────────────────────────────────────────────

function roleIcon(role: OrganizationRole) {
  switch (role) {
    case 'ORG_OWNER': return Crown;
    case 'ORG_ADMIN': return ShieldCheck;
    case 'ORG_MEMBER': return Shield;
    case 'ORG_VIEWER': return Eye;
    default: return Shield;
  }
}

export function RoleBadgeInline({ role }: { role: OrganizationRole }) {
  const Icon = roleIcon(role);
  return (
    <span className="inline-flex items-center gap-1.5 rounded bg-white/[0.05] px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-ink-secondary">
      <Icon className="h-3 w-3" strokeWidth={2.5} />
      {orgRoleDisplayName(role)}
    </span>
  );
}

export function OrgPanel({
  orgId,
  org,
  role,
  joinedAt,
  onLeft,
  onRequestElevation,
}: {
  orgId: string;
  org: OrganizationSummary | undefined;
  role?: OrganizationRole;
  joinedAt?: number;
  onLeft?: () => void;
  onRequestElevation?: () => void;
}) {
  const [error, setError] = useState('');
  const leaveMut = useMutation({
    mutationFn: () => api.leaveOrg(orgId),
    onSuccess: () => {
      setError('');
      onLeft?.();
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  const isOwner = role === 'ORG_OWNER';
  const canRequestElevation = role === 'ORG_MEMBER' || role === 'ORG_ADMIN';
  const canManageClans = role === 'ORG_OWNER' || role === 'ORG_ADMIN' || role === undefined;

  const onLeave = () => {
    if (isOwner) {
      window.alert('Org owners must transfer ownership before leaving. The transfer-leadership flow ships in a later phase — DM Luke or Lisa on Discord for now.');
      return;
    }
    if (!window.confirm(`Leave ${org?.name ?? orgId}? You'll lose access to this org's competitions and admin surfaces. You can be re-invited later.`)) return;
    leaveMut.mutate();
  };

  return (
    <Card>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-2">
            <Building2 className="h-4 w-4 text-ink-secondary" />
            <h2 className="truncate font-display text-lg font-black uppercase tracking-wide text-ink-primary">
              {org?.name ?? orgId.replace(/^ORG#/, '')}
            </h2>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {role && <RoleBadgeInline role={role} />}
            {joinedAt && (
              <span className="font-mono text-[10px] uppercase tracking-wider text-ink-meta">
                joined {new Date(joinedAt).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="mb-4 rounded-lg bg-white/[0.04] p-4">
        <p className="font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-ink-meta">
          Recent activity
        </p>
        <p className="mt-2 text-xs text-ink-secondary">
          No recent activity yet. (Activity feed lands in a future phase.)
        </p>
      </div>

      {error && (
        <p className="mb-3 flex items-start gap-2 text-xs text-red-400">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{error}</span>
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Link
          to={`/orgs/${encodeURIComponent(orgId)}/credits`}
          className={`btn-ghost ${MORPH} inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-wider`}
        >
          Credits
        </Link>
        {canManageClans && (
          <Link
            to={`/admin/clans?orgId=${encodeURIComponent(orgId)}`}
            className={`btn-ghost ${MORPH} inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-wider`}
          >
            Manage clans
          </Link>
        )}
        {(role === 'ORG_OWNER' || role === 'ORG_ADMIN') && (
          // `from` is where the veto-config save on that page returns the
          // user to (see OrgBranding.tsx's VetoConfigCard) — this is the
          // ONLY place that link is opened from, so it always carries the
          // return trip; a direct/bookmarked visit falls back to the same
          // path on the destination side.
          <Link
            to={`/orgs/${encodeURIComponent(orgId)}/branding?from=${encodeURIComponent(orgSetupReturnPath())}`}
            className={`btn-ghost ${MORPH} inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-wider`}
          >
            Branding & veto
          </Link>
        )}
        {canRequestElevation && onRequestElevation && (
          <button
            type="button"
            onClick={onRequestElevation}
            className={`btn-ghost ${MORPH} inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-wider`}
          >
            <ShieldCheck className="h-3.5 w-3.5" strokeWidth={2.5} />
            Request elevation
          </button>
        )}
        {onLeft && role && (
          <button
            type="button"
            onClick={onLeave}
            disabled={leaveMut.isPending || isOwner}
            title={isOwner ? 'Transfer ownership before leaving' : undefined}
            className={`btn-ghost ${MORPH} ml-auto inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-wider text-red-300 hover:text-red-200 disabled:opacity-50`}
          >
            <LogOut className="h-3.5 w-3.5" strokeWidth={2.5} />
            {leaveMut.isPending ? 'Leaving…' : 'Leave org'}
          </button>
        )}
      </div>
    </Card>
  );
}
